export function exportVerificationJson(result) {
  return JSON.stringify(result?.raw || result, null, 2);
}

export function exportVerificationCsv(result) {
  const rows = [["Service", "Status", "Latency (ms)", "Message"]];
  (result?.checks || []).forEach((c) => {
    rows.push([c.service, c.status, String(c.latencyMs ?? ""), `"${(c.message || "").replace(/"/g, '""')}"`]);
  });
  rows.push([]);
  rows.push(["Health Score", String(result?.healthScore ?? ""), "", ""]);
  rows.push(["Total (ms)", String(result?.responseTimeMs ?? ""), "", ""]);
  rows.push(["Verified At", result?.checkedAt || "", "", ""]);
  if (result?.permissions) {
    rows.push([]);
    rows.push(["Permission", "Status", "", "Message"]);
    Object.entries(result.permissions).forEach(([k, v]) => {
      rows.push([k, v.status, "", `"${(v.message || "").replace(/"/g, '""')}"`]);
    });
  }
  return rows.map((r) => r.join(",")).join("\n");
}

export function exportVerificationHtml(result) {
  const checks = (result?.checks || [])
    .map(
      (c) =>
        `<tr><td>${c.service}</td><td>${c.status}</td><td>${c.latencyMs || ""}</td><td>${escapeHtml(c.message || "")}</td></tr>`
    )
    .join("");

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Firebase Health Check Report</title>
<style>
body{font-family:system-ui,sans-serif;margin:2rem;color:#111}
h1{font-size:1.5rem} table{border-collapse:collapse;width:100%;margin:1rem 0}
th,td{border:1px solid #ddd;padding:8px;text-align:left} th{background:#f5f5f5}
.score{font-size:2rem;font-weight:700;color:#059669}
</style></head><body>
<h1>Firebase Health Check Report</h1>
<p class="score">${result?.healthScore ?? 0}/100</p>
<p>Project: ${escapeHtml(result?.advanced?.projectId || "")} · ${result?.responseTimeMs}ms · ${result?.checkedAt || ""}</p>
<table><thead><tr><th>Service</th><th>Status</th><th>Latency</th><th>Message</th></tr></thead>
<tbody>${checks}</tbody></table>
<p><small>Generated by Firestore Studio Connection Verification</small></p>
</body></html>`;
}

export async function exportVerificationPdf(result, filename = "health-check.pdf") {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  let y = 16;
  doc.setFontSize(16);
  doc.text("Firebase Health Check Report", 14, y);
  y += 10;
  doc.setFontSize(12);
  doc.text(`Health Score: ${result?.healthScore ?? 0}/100`, 14, y);
  y += 8;
  doc.text(`Total: ${result?.responseTimeMs ?? 0}ms`, 14, y);
  y += 8;
  doc.text(`Project: ${result?.advanced?.projectId || ""}`, 14, y);
  y += 12;
  doc.setFontSize(10);
  (result?.checks || []).forEach((c) => {
    if (y > 270) { doc.addPage(); y = 16; }
    doc.text(`${c.service}: ${c.status} (${c.latencyMs || 0}ms)`, 14, y);
    y += 6;
    const lines = doc.splitTextToSize(c.message || "", 180);
    doc.text(lines, 18, y);
    y += lines.length * 5 + 4;
  });
  doc.save(filename);
}

export function exportVerificationMarkdown(result) {
  if (!result) return "";
  const lines = [];
  lines.push("# Firebase Health Check Report");
  lines.push("");
  lines.push(`**Health Score:** ${result.healthScore ?? 0}%  `);
  lines.push(`**Project:** ${result.advanced?.projectId || "—"}  `);
  lines.push(`**Total:** ${result.responseTimeMs ?? 0} ms  `);
  lines.push(`**Verified:** ${result.checkedAt || "—"}  `);
  lines.push(`**SDK:** ${result.advanced?.sdkVersion || "—"} · ${result.advanced?.browser || ""} / ${result.advanced?.os || ""}`);
  lines.push("");
  lines.push("## Services");
  lines.push("");
  lines.push("| Service | Status | Latency | Message |");
  lines.push("| --- | --- | --- | --- |");
  (result.checks || []).forEach((c) => {
    lines.push(`| ${c.service} | ${c.status} | ${c.latencyMs || 0} ms | ${(c.message || "").replace(/\|/g, "\\|")} |`);
  });
  if (result.permissions) {
    lines.push("");
    lines.push("## Permissions");
    lines.push("");
    Object.entries(result.permissions).forEach(([k, v]) => {
      lines.push(`- **${k}**: ${v.status} — ${v.message}`);
    });
  }
  if (result.security?.length) {
    lines.push("");
    lines.push("## Security");
    lines.push("");
    result.security.forEach((f) => lines.push(`- [${f.severity}] ${f.message}`));
  }
  if (result.warnings?.length) {
    lines.push("");
    lines.push("## Warnings");
    lines.push("");
    result.warnings.forEach((w) => lines.push(`- ${w}`));
  }
  return lines.join("\n");
}

export function formatVerificationReport(result) {
  if (!result) return "";
  const lines = [];
  lines.push("Firebase Health Check Suite Report");
  lines.push(`Health Score: ${result.healthScore}/100`);
  lines.push(`Total: ${result.responseTimeMs}ms`);
  lines.push("");
  (result.checks || []).forEach((c) => {
    const icon =
      c.status === "success" ? "✔" :
      c.status === "not_configured" || c.status === "skipped" ? "○" :
      c.status === "read_only" || c.status === "permission_denied" ? "⚠" :
      c.status === "failed" ? "✘" : "⚠";
    lines.push(`${c.service}: ${icon} ${c.status}${c.latencyMs ? ` · ${c.latencyMs}ms` : ""}`);
    if (c.message) lines.push(`  ${c.message}`);
  });
  if (result.permissions) {
    lines.push("");
    lines.push("Permissions:");
    Object.entries(result.permissions).forEach(([k, v]) => {
      lines.push(`  ${k}: ${v.status} — ${v.message}`);
    });
  }
  if (result.warnings?.length) {
    lines.push("");
    lines.push("Warnings:");
    result.warnings.forEach((w) => lines.push(`  • ${w}`));
  }
  return lines.join("\n");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
